import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, MenuItem, Select, TextField, Typography } from "@mui/material";
import { formatCurrency } from "../../utils/formatCurrency";
import { EMPTY_ADDRESS, addressIncomplete } from "../../utils/returnAddress";
import { AcceptBody, MAX_TEXT_LENGTH } from "./useInquiryDetail";
import ReturnAddressFields from "./ReturnAddressFields";
import DialogSuccess from "./DialogSuccess";
import { BUTTON, DIALOG, FIELD, FIELD_LABEL, INK, OUTLINED } from "./styles";

type Props = {
  open: boolean;
  onClose: () => void;
  amount: number;
  currency: string;
  acceptTypes: string[];
  onSubmit: (body: AcceptBody) => Promise<boolean>;
  submitting: boolean;
  error: string | null;
};

export default function AcceptClaimDialog({ open, onClose, amount, currency, acceptTypes, onSubmit, submitting, error }: Props) {
  const [acceptType, setAcceptType] = useState(acceptTypes[0] ?? '');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [done, setDone] = useState(false);
  const partial = acceptType === 'PARTIAL_REFUND';
  const withReturn = acceptType === 'REFUND_WITH_RETURN';
  const addressMissing = withReturn && addressIncomplete(address);
  const noteOver = note.length > MAX_TEXT_LENGTH;
  const cents = Math.round(parseFloat(value) * 100);
  const valid = cents > 0 && cents <= amount;
  const refund = partial ? (valid ? cents : 0) : amount;
  const labels: Record<string, string> = {
    REFUND: `Full, ${formatCurrency(amount, currency)}`,
    PARTIAL_REFUND: "Partial",
    REFUND_WITH_RETURN: "Full after return",
  };

  const submit = async () => {
    if (await onSubmit({ acceptType, ...(partial && { amount: cents }), ...(withReturn && { returnAddress: address }), note: note.trim(), idempotencyKey })) setDone(true);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, why) => { if (!submitting && (done || why !== 'backdropClick')) onClose(); }}
      maxWidth={false}
      aria-labelledby="accept-dialog-title"
      slotProps={{ paper: { sx: DIALOG } }}
    >
      {done ? (
        <DialogSuccess
          titleId="accept-dialog-title"
          title={withReturn ? "Claim accepted" : `Refunded ${formatCurrency(refund, currency)}`}
          detail={withReturn ? "The refund follows once the buyer returns the item." : "PayPal closed the inquiry in the buyer's favour."}
          onClose={onClose}
        />
      ) : (
        <>
      <Box>
        <Typography id="accept-dialog-title" component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>Accept claim and refund</Typography>
        <Typography sx={{ fontSize: 13, color: INK.muted, mt: '4px' }}>
          {withReturn
            ? "Gives up the case. The buyer returns the item, then PayPal refunds them and closes the inquiry in their favour."
            : "Gives up the case. PayPal refunds the buyer at once and closes the inquiry in their favour. No buyer action needed."}
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
        <Box>
          <Box component="label" id="accept-type-label" sx={FIELD_LABEL}>Refund</Box>
          <Select size="small" disabled={submitting} fullWidth labelId="accept-type-label" value={acceptType} onChange={(e) => setAcceptType(e.target.value)} sx={{ fontSize: 13, borderRadius: '8px' }}>
            {acceptTypes.map((type) => <MenuItem key={type} value={type} sx={{ fontSize: 13 }}>{labels[type] ?? type}</MenuItem>)}
          </Select>
        </Box>
        {partial && (
          <Box>
            <Box component="label" htmlFor="accept-amount" sx={FIELD_LABEL}>Amount</Box>
            <TextField
              id="accept-amount"
              disabled={submitting}
              type="number"
              size="small"
              fullWidth
              value={value}
              onChange={(e) => setValue(e.target.value)}
              slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
              helperText={currency.toUpperCase()}
              sx={FIELD}
            />
          </Box>
        )}
        {withReturn && <ReturnAddressFields value={address} onChange={setAddress} disabled={submitting} />}
      </Box>
      <Box>
        <Box component="label" htmlFor="accept-note" sx={FIELD_LABEL}>Note to the buyer</Box>
        <TextField
          id="accept-note"
          disabled={submitting}
          fullWidth
          multiline
          minRows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ ...FIELD, '& .MuiInputBase-root': { ...FIELD['& .MuiInputBase-root'], lineHeight: 1.5 } }}
        />
        <Box sx={{ fontSize: 12, color: noteOver ? INK.danger : INK.muted, textAlign: 'right', mt: '4px' }}>
          {note.length.toLocaleString('en-US')} / {MAX_TEXT_LENGTH.toLocaleString('en-US')}
        </Box>
      </Box>
      <Box sx={{ bgcolor: '#fffaeb', border: '1px solid #fedf89', borderRadius: '8px', p: '10px 12px', fontSize: 12, color: '#93370d' }}>
        Cannot be undone. The subscription stays active unless you cancel it separately.
      </Box>
      <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" sx={OUTLINED} disabled={submitting} onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" size="small" sx={BUTTON} disabled={submitting || (partial && !valid) || addressMissing || !note.trim() || noteOver} onClick={submit}>
          {submitting
            ? <><CircularProgress size={14} color="inherit" sx={{ mr: '8px' }} />{withReturn ? 'Accepting…' : `Refunding ${formatCurrency(refund, currency)}…`}</>
            : withReturn ? `Accept, refund ${formatCurrency(refund, currency)} after return` : `Refund ${formatCurrency(refund, currency)} and close`}
        </Button>
      </Box>
        </>
      )}
    </Dialog>
  );
}
